import { Chip } from '@mui/material';
import sortBy from 'lodash/sortBy';
import { useMemo } from 'react';
import { LinearProgress, useNotify } from 'react-admin';
import {
  Label,
  useDeleteCampaignLabelMutation,
  useDeleteLineItemLabelMutation,
  useGetLabelsForCampaignQuery,
  useGetLabelsForLineItemByNativeLineItemIdQuery,
  useGetLabelsForLineItemByPlacementsLineItemIdQuery,
} from 'src/generated/client';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  getLineItemLabelLookupIds,
  LineItemLabelLineItemRecord,
} from 'src/client/utility/pio-native-lineitem-fk-reads';

/** Minimal campaign shape for exclusion chips (native or legacy show). */
export type CampaignExclusionLabelRecord = {
  id?: string | number;
  externalPioId?: string | null;
};

type CampaignLabelChip = Label & {
  campaignLabelPlacementsCampaignId?: string | null;
};

const skippedNativeLineItemId = '00000000-0000-0000-0000-000000000000';

type LineItemLabelChip = Label & {
  lineItemLabelPlacementsLineItemId?: string | null;
};

type LineItemExclusionLabelChipsProps = { record: LineItemLabelLineItemRecord; deletionEnabled?: boolean };

export const LineItemExclusionLabelChips = ({ record, deletionEnabled = false }: LineItemExclusionLabelChipsProps) => {
  const notify = useNotify();
  const { placementsLineItemId, nativeLineItemId } = getLineItemLabelLookupIds(record);

  const nativeQuery = useGetLabelsForLineItemByNativeLineItemIdQuery({
    variables: { lineItemId: nativeLineItemId ?? skippedNativeLineItemId },
    skip: !nativeLineItemId,
  });
  const nativeLabels = nativeQuery.data?.lineItemLabels?.nodes ?? [];
  const shouldFallbackToLegacy =
    Boolean(placementsLineItemId) &&
    (!nativeLineItemId || Boolean(nativeQuery.error) || (!nativeQuery.loading && nativeLabels.length === 0));
  const fallbackLegacyQuery = useGetLabelsForLineItemByPlacementsLineItemIdQuery({
    variables: { placementsLineItemId: placementsLineItemId ?? '' },
    skip: !shouldFallbackToLegacy,
  });

  const [deleteLineItemLabelMutation, { loading: deleteLoading, error: deleteError }] = useDeleteLineItemLabelMutation({
    onCompleted: () => {
      if (nativeLineItemId) nativeQuery.refetch();
      if (shouldFallbackToLegacy && placementsLineItemId) fallbackLegacyQuery.refetch();
    },
  });

  const handleDelete = async (label: LineItemLabelChip, event: React.MouseEvent) => {
    event.stopPropagation();

    const labelPlacementsLineItemId = label.lineItemLabelPlacementsLineItemId ?? placementsLineItemId;

    if (!labelPlacementsLineItemId) {
      notify('Error deleting line item label', { type: 'warning' });
      return;
    }

    try {
      await deleteLineItemLabelMutation({
        variables: {
          placementsLineItemId: labelPlacementsLineItemId,
          labelId: label.id,
        },
      });
      notify('Line item label deleted successfully');
    } catch (error) {
      console.error('Error deleting line item label:', error);
      notify('Error deleting line item label', { type: 'warning' });
    }
  };

  const lineItemLabels = nativeLabels.length > 0 ? nativeLabels : fallbackLegacyQuery.data?.lineItemLabels?.nodes;
  const loading = nativeQuery.loading || (shouldFallbackToLegacy && fallbackLegacyQuery.loading);

  if (loading || !lineItemLabels) return <LinearProgress />;

  const labels = lineItemLabels
    .filter(node => node.label)
    .map(node => ({
      ...(node.label as Label),
      lineItemLabelPlacementsLineItemId: node.placementsLineItemId,
    }));

  return (
    <ExclusionLabelChips
      labels={labels}
      deleteLoading={deleteLoading}
      deletionEnabled={deletionEnabled}
      handleDelete={handleDelete}
    />
  );
};

type CampaignExclusionLabelChipsProps = {
  record: CampaignExclusionLabelRecord;
  deletionEnabled?: boolean;
};

/**
 * Campaign exclusion chips. Dual-reads by native campaign UUID and externalPioId so
 * historical labels keyed in either id-space still show on native CampaignShow (MD-5188).
 */
export const CampaignExclusionLabelChips = ({ record, deletionEnabled = false }: CampaignExclusionLabelChipsProps) => {
  const notify = useNotify();
  const campaignId = record?.id != null ? String(record.id) : '';
  const externalPioId = record?.externalPioId ? String(record.externalPioId) : '';
  const shouldQueryLegacy = Boolean(externalPioId && externalPioId !== campaignId);

  const primaryQuery = useGetLabelsForCampaignQuery({
    variables: { campaignId },
    skip: !campaignId,
  });
  const legacyQuery = useGetLabelsForCampaignQuery({
    variables: { campaignId: externalPioId },
    skip: !shouldQueryLegacy,
  });

  const [deleteCampaignLabelMutation, { loading: deleteLoading }] = useDeleteCampaignLabelMutation({
    onCompleted: () => {
      primaryQuery.refetch();
      if (shouldQueryLegacy) legacyQuery.refetch();
    },
  });

  const labels = useMemo(() => {
    const nodes = [
      ...(primaryQuery.data?.campaignLabels?.nodes ?? []),
      ...(shouldQueryLegacy ? legacyQuery.data?.campaignLabels?.nodes ?? [] : []),
    ];
    const byLabelId = new Map<string, CampaignLabelChip>();
    for (const node of nodes) {
      if (!node.label) continue;
      const id = String(node.label.id);
      if (byLabelId.has(id)) continue;
      byLabelId.set(id, {
        ...(node.label as Label),
        campaignLabelPlacementsCampaignId: node.placementsCampaignId,
      });
    }
    return Array.from(byLabelId.values());
  }, [primaryQuery.data, legacyQuery.data, shouldQueryLegacy]);

  const handleDelete = async (label: CampaignLabelChip, event: React.MouseEvent) => {
    event.stopPropagation();

    const placementsCampaignId =
      label.campaignLabelPlacementsCampaignId ?? campaignId ?? externalPioId;

    if (!placementsCampaignId) {
      notify('Error deleting campaign label', { type: 'warning' });
      return;
    }

    try {
      await deleteCampaignLabelMutation({
        variables: {
          placementsCampaignId,
          labelId: label.id,
        },
      });
      notify('Campaign label deleted successfully');
    } catch (error) {
      console.error('Error deleting campaign label:', error);
      notify(`Error deleting campaign label`, { type: 'warning' });
    }
  };

  const loading =
    Boolean(campaignId && primaryQuery.loading) || (shouldQueryLegacy && legacyQuery.loading);
  const primaryReady = !campaignId || primaryQuery.data?.campaignLabels != null || primaryQuery.error;
  const legacyReady = !shouldQueryLegacy || legacyQuery.data?.campaignLabels != null || legacyQuery.error;

  if (loading || !primaryReady || !legacyReady) return <LinearProgress />;

  return (
    <ExclusionLabelChips
      labels={labels}
      deleteLoading={deleteLoading}
      deletionEnabled={deletionEnabled}
      handleDelete={handleDelete}
    />
  );
};

type ExclusionLabelChipsProps = {
  labels: Array<Label | LineItemLabelChip | CampaignLabelChip>;
  deleteLoading: boolean;
  deletionEnabled?: boolean;
  handleDelete: (label: Label | LineItemLabelChip | CampaignLabelChip, event: React.MouseEvent) => void;
};

const ExclusionLabelChips = ({
  labels,
  deleteLoading,
  deletionEnabled = false,
  handleDelete,
}: ExclusionLabelChipsProps) => {
  const sortLabelsByName = (labels: Label[]) => sortBy(labels, [label => label?.name?.toLowerCase()]);
  const sortedLabels = sortLabelsByName(labels);
  const activeLabels = sortedLabels.filter(label => !label.isArchived);

  return (
    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
      {activeLabels?.map(label => (
        <Chip
          key={label.id}
          label={label?.name}
          onDelete={deletionEnabled ? event => handleDelete(label, event) : undefined}
          deleteIcon={deletionEnabled ? <CancelIcon /> : undefined}
          disabled={deleteLoading}
        />
      ))}
    </div>
  );
};
